import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

/**
 * Status page pública.
 *
 * Polla /api/health/ready a cada 30s e mostra status agregado por
 * serviço. Histórico de últimos 60min em memória (gráfico de barras
 * simples). Não persiste — se a página recarrega, histórico zera.
 *
 * Pra histórico real (90 dias), apontar Better Stack/UptimeRobot pra
 * /api/health/ready (já testamos esse fluxo).
 */

interface HealthResponse {
  status: 'ready' | 'degraded'
  timestamp: string
  checks: Record<string, 'ok' | 'down'>
}

interface HistoryEntry {
  t: number
  status: 'ready' | 'degraded' | 'unknown'
  checks: Record<string, 'ok' | 'down'>
}

const SERVICE_LABEL: Record<string, string> = {
  db:      'Banco de dados (PostgreSQL)',
  redis:   'Filas e cache (Redis)',
  storage: 'Storage de PDFs (MinIO)',
  cert:    'Certificado PAdES',
}

export default function Status() {
  const [current, setCurrent] = useState<HealthResponse | null>(null)
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [error, setError] = useState('')

  async function poll() {
    try {
      const res = await fetch('/api/health/ready')
      const body = await res.json() as HealthResponse
      setCurrent(body)
      setError('')
      setHistory(prev => {
        const next = [...prev, { t: Date.now(), status: body.status, checks: body.checks }]
        // Mantém últimos 120 (1h se polling a cada 30s)
        return next.slice(-120)
      })
    } catch (err: any) {
      setError(err.message || 'Falha checando status')
      setHistory(prev => {
        const next = [...prev, { t: Date.now(), status: 'unknown' as const, checks: {} }]
        return next.slice(-120)
      })
    }
  }

  useEffect(() => {
    poll()
    const id = setInterval(poll, 30_000)
    return () => clearInterval(id)
  }, [])

  const overall = current?.status ?? (error ? 'degraded' : 'unknown')
  const overallColor =
    overall === 'ready'    ? 'bg-green-500'  :
    overall === 'degraded' ? 'bg-red-500'    :
                             'bg-gray-300'
  const overallLabel =
    overall === 'ready'    ? 'Operando normalmente' :
    overall === 'degraded' ? 'Degradação parcial'   :
                             'Status desconhecido'

  // Uptime aproximado em janela observada (entradas com status=ready)
  const uptimePct = history.length === 0 ? null :
    (history.filter(h => h.status === 'ready').length / history.length) * 100

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-indigo-600">PAKT</Link>
          <span className="text-sm text-gray-500">status.paktdoc.com</span>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <div className={`rounded-xl border ${overall === 'ready' ? 'border-green-200 bg-green-50' : overall === 'degraded' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'} p-6 mb-6 flex items-center gap-4`}>
          <div className={`w-4 h-4 rounded-full ${overallColor} animate-pulse`} />
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900">{overallLabel}</h1>
            {current && (
              <p className="text-xs text-gray-500 font-mono mt-1">
                Última checagem: {new Date(current.timestamp).toLocaleString('pt-BR')}
                {uptimePct !== null && ` · uptime observado ${uptimePct.toFixed(1)}% (${history.length} amostras)`}
              </p>
            )}
            {error && <p className="text-xs text-red-700 mt-1">Falha: {error}</p>}
          </div>
        </div>

        {/* Serviços */}
        <section className="bg-white rounded-xl border border-gray-200 mb-6">
          <h2 className="px-6 py-3 border-b border-gray-200 font-semibold text-gray-900">Serviços</h2>
          {current ? (
            <ul className="divide-y divide-gray-100">
              {Object.entries(current.checks).map(([key, status]) => (
                <li key={key} className="px-6 py-3 flex items-center justify-between">
                  <span className="text-sm text-gray-700">{SERVICE_LABEL[key] || key}</span>
                  <span className={`text-xs font-mono uppercase tracking-wider px-2 py-1 rounded ${
                    status === 'ok' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {status === 'ok' ? 'OK' : 'DOWN'}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-6 py-4 text-sm text-gray-500">Carregando...</p>
          )}
        </section>

        {/* Histórico em barras (uptime visual) */}
        {history.length > 0 && (
          <section className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <h2 className="font-semibold text-gray-900 mb-3">
              Últimos {history.length} checks (1 a cada 30s desde que esta página foi aberta)
            </h2>
            <div className="flex gap-0.5 h-12">
              {history.map((h, i) => {
                const color =
                  h.status === 'ready' ? 'bg-green-500' :
                  h.status === 'degraded' ? 'bg-red-500' :
                  'bg-gray-300'
                const time = new Date(h.t).toLocaleTimeString('pt-BR')
                const failedChecks = Object.entries(h.checks).filter(([_, s]) => s === 'down').map(([k]) => k)
                const title = `${time} · ${h.status}${failedChecks.length ? ' · falhou: ' + failedChecks.join(', ') : ''}`
                return <div key={i} className={`flex-1 ${color} rounded-sm`} title={title} />
              })}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Histórico em memória (zera ao recarregar). Pra uptime histórico de 90 dias,
              apontamos UptimeRobot externo pra <code className="bg-gray-100 px-1">/api/health/ready</code>.
            </p>
          </section>
        )}

        <div className="text-center text-sm text-gray-500">
          Reportar incidente: <a href="mailto:suporte@pakt.io" className="text-indigo-600 hover:underline">suporte@pakt.io</a>
          {' · '}
          <Link to="/ajuda" className="text-indigo-600 hover:underline">Central de Ajuda</Link>
        </div>
      </main>
    </div>
  )
}
